/* Crystal Quest header behaviour (NAV v2), first ported from the homepage-navigation prototype (assets/js/chrome.js):
   top bar rotation (only with two or more messages), compact sticky bar, mega panels with hover intent, ZIP hardness check,
   lazy panel images (src and srcset), and the phone sheet with level 2 section views and level 3 dropdowns. The top bar
   Help menu is gone (AJ 2026-10-10: Help lives in Resources); the phone action bar is gone (AJ 2026-10-11 09:33).
   Null-safe; one listener set per element. */
if (!customElements.get('cq-header')) {
  customElements.define('cq-header', class extends HTMLElement {
    connectedCallback() {
      if (this.cqEvents) return;
      this.cqEvents = new AbortController();
      this.on = (target, type, fn, opts) => target && target.addEventListener(type, fn, Object.assign({ signal: this.cqEvents.signal }, opts || {}));
      this.mqDesktop = matchMedia('(min-width: 1024px)');
      this.mqHover = matchMedia('(hover: hover) and (pointer: fine)');
      this.mqReduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.nav = this.querySelector('[data-nav]');
      this.initUtility();
      if (this.nav) { this.initCompact(); this.initPanels(); this.initAccount(); }
      this.initZip();
      this.initMobile();
      this.initHelp();
      this.on(document, 'cart-drawer:before-open', () => this.closeAll());
      this.initEditor();
    }

    /* Theme editor only: selecting a Menu panel block, or one of its photo cards, opens that drop-down on desktop so the
       merchant sees what they are editing; deselecting closes it. */
    initEditor() {
      if (!window.Shopify || !window.Shopify.designMode || !this.openPanel) return;
      this.on(document, 'shopify:block:select', (e) => {
        const item = this.contains(e.target) && e.target.closest('.nav-item');
        const name = item && item.querySelector('[data-nav-trigger]')?.getAttribute('data-nav-trigger');
        if (name && this.mqDesktop.matches) this.openPanel(name, 'click');
      });
      this.on(document, 'shopify:block:deselect', (e) => {
        if (this.contains(e.target) && e.target.closest('.nav-item')) this.closePanel?.(false);
      });
    }

    disconnectedCallback() {
      this.closeAcct?.(false);
      this.closeAcct = null;
      this.closeHelp?.(false);
      this.closeHelp = null;
      this.closeMobile?.(false);
      this.cqEvents?.abort();
      this.cqEvents = null;
      clearInterval(this.rotation);
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      clearTimeout(this.switchTimer);
      clearTimeout(this.morphTimer);
      clearTimeout(this.revealTimer);
    }

    isDesktop() { return this.mqDesktop.matches; }
    canHover() { return this.mqHover.matches; }
    reduced() { return this.mqReduced.matches; }

    /* ---------- Lazy images: closed panels never request images ---------- */
    loadImages(root) {
      (root || this).querySelectorAll('img[data-src]').forEach((image) => {
        if (image.dataset.srcset) image.srcset = image.dataset.srcset;
        image.src = image.dataset.src;
        delete image.dataset.src;
        delete image.dataset.srcset;
      });
    }

    /* ---------- Utility row ---------- */
    initUtility() {
      const rot = this.querySelector('[data-util-rot]');
      const items = rot ? Array.from(rot.querySelectorAll('.util-rot__item')) : [];
      if (items.length < 2) return;
      let i = 0;
      let hovering = false;
      const util = rot.closest('[data-util]') || rot;
      const show = (n) => items.forEach((el, k) => { el.classList.toggle('is-on', k === n); el.setAttribute('aria-hidden', k === n ? 'false' : 'true'); });
      this.on(util, 'mouseenter', () => { hovering = true; });
      this.on(util, 'mouseleave', () => { hovering = false; });
      this.on(util, 'focusin', () => { hovering = true; });
      this.on(util, 'focusout', () => { hovering = false; });
      this.rotation = setInterval(() => {
        if (hovering || document.hidden || this.reduced()) return;
        i = (i + 1) % items.length;
        show(i);
      }, 5000);
    }

    /* ---------- Sticky compact bar (transform only; the bar keeps its box) ---------- */
    initCompact() {
      let ticking = false;
      const update = () => { ticking = false; this.nav.classList.toggle('is-compact', window.scrollY > 40); };
      this.on(window, 'scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
      update();
    }

    /* ---------- Mega panels ---------- */
    initPanels() {
      const nav = this.nav;
      const triggers = Array.from(nav.querySelectorAll('[data-nav-trigger]'));
      if (!triggers.length) return;
      const scrim = this.querySelector('[data-nav-scrim]');
      const panelOf = (name) => nav.querySelector(`[data-nav-panel="${name}"]`);
      const triggerOf = (name) => nav.querySelector(`[data-nav-trigger="${name}"]`);
      const panels = Array.from(nav.querySelectorAll('[data-nav-panel]'));
      const order = (name) => triggers.findIndex((t) => t.getAttribute('data-nav-trigger') === name);
      let openName = null;
      let openedVia = null;
      let openedAt = 0;
      let closedName = null;
      let closedAt = 0;

      /* Motion (M3, AJ 2026-10-11; NB 09:12): one shared surface under the panels, a gliding indicator under the top items,
         a staggered rise on first open and a staggered slide in the direction of travel on a switch. CSS owns the curves (assets/cq-header.css,
         "Motion layer"); reduced motion makes every step instant. */
      const surface = nav.querySelector('[data-nav-surface]');
      const ind = nav.querySelector('[data-nav-ind]');
      const fx = !!surface;
      if (fx) nav.classList.add('has-fx');
      const ITEMS = '.nav-tiles > li, .nav-cols > *, .nav-side, .nav-zip--strip, .nav-ci__art, .nav-more, .nav-gcards > li, .nav-rs__lists > *, .nav-shop-grid > *';
      /* mark the reveal items once, outermost only, so their closed state is painted before the first open */
      if (fx) panels.forEach((p) => {
        const items = Array.from(p.querySelectorAll(ITEMS));
        items.filter((el) => !items.some((o) => o !== el && o.contains(el))).forEach((el) => el.setAttribute('data-m', ''));
      });
      /* each item's delay follows its column (AJ 2026-10-11 09:12 "no fading or effect ... id like to see"): 36 ms per
         column from the left (--d) and from the right (--dr, a switch to the right), up to 180 ms, so the wave is easy to see
         and ends inside 450 ms (measured once per width) */
      const prep = (panel) => {
        if (!fx || !panel || panel.fxWidth === window.innerWidth) return;
        const inner = panel.querySelector('.nav-panel__inner') || panel;
        if (!inner.getBoundingClientRect().width) return;
        panel.fxWidth = window.innerWidth;
        /* only items on screen count (tiles past the width tier are display: none and would read as x 0) */
        const items = Array.from(panel.querySelectorAll('[data-m]')).filter((el) => el.getClientRects().length);
        const cols = items.map((el) => Math.round(el.getBoundingClientRect().left / 24));
        const ranks = Array.from(new Set(cols)).sort((a, b) => a - b);
        items.forEach((el, i) => {
          const r = ranks.indexOf(cols[i]);
          el.style.setProperty('--d', Math.min(r, 5) * 36 + 'ms');
          el.style.setProperty('--dr', Math.min(ranks.length - 1 - r, 5) * 36 + 'ms');
        });
      };
      const setSurface = (h) => {
        if (!surface) return;
        surface.classList.remove('is-morph');
        surface.style.height = h + 'px';
      };
      /* a switch eases the surface's real height (CSS .is-morph), so its rounded corners and shadow never stretch */
      const morph = (from, h) => {
        if (!from || !h) { setSurface(h); return; }
        surface.classList.add('is-morph');
        surface.style.height = h + 'px';
        clearTimeout(this.morphTimer);
        this.morphTimer = setTimeout(() => { if (openName) setSurface(panelOf(openName).offsetHeight); }, 280);
      };
      const placeInd = (name, glide) => {
        const t = triggerOf(name);
        if (!ind || !t) return;
        const bar = nav.querySelector('.nav-bar').getBoundingClientRect();
        const range = document.createRange();
        range.selectNodeContents(t);
        const r = range.getBoundingClientRect();
        const box = r.width ? r : t.getBoundingClientRect();
        ind.classList.toggle('is-glide', !!glide);
        ind.style.setProperty('--ix', Math.round(box.left - bar.left) + 'px');
        ind.style.setProperty('--iw', String(Math.round(box.width)));
      };
      /* the bottom clip that keeps a panel's content inside the surface while its height moves (0 clears it) */
      const clip = (p, b) => { p.style.clipPath = b > 0 ? `inset(0px 0px ${Math.round(b)}px 0px)` : ''; };
      const finishSwitch = () => {
        clearTimeout(this.switchTimer);
        nav.classList.remove('is-switching');
        ind?.classList.remove('is-glide');
        panels.forEach((p) => {
          p.classList.remove('is-from-l', 'is-from-r', 'is-in-l', 'is-in-r');
          clip(p, 0);
          if (p.classList.contains('is-open')) return;
          p.classList.remove('is-to-l', 'is-to-r');
          p.inert = false;
        });
      };
      if (fx && 'ResizeObserver' in window) {
        const ro = new ResizeObserver(() => {
          if (openName && !surface.classList.contains('is-morph')) setSurface(panelOf(openName).offsetHeight);
        });
        panels.forEach((p) => ro.observe(p));
        this.cqEvents.signal.addEventListener('abort', () => ro.disconnect(), { once: true });
      }
      const setBottom = () => {
        const bar = nav.querySelector('.nav-bar');
        if (bar) nav.style.setProperty('--nav-bottom', Math.round(bar.getBoundingClientRect().bottom) + 'px');
      };
      /* the panel being opened (or about to open on hover intent) loads its images at once; the rest load when the
         browser is idle after the pointer or focus first reaches the menu */
      const hydrate = () => {
        if (this.hydrated) return;
        this.hydrated = true;
        const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));
        idle(() => this.loadImages(nav), { timeout: 1500 });
      };
      const open = (name, via) => {
        clearTimeout(this.openTimer); clearTimeout(this.closeTimer);
        if (!this.isDesktop()) return;
        const panel = panelOf(name);
        if (!panel || openName === name) return;
        this.closeAcct?.(false);
        this.closeHelp?.(false);
        this.loadImages(panel);
        hydrate();
        prep(panel);
        /* a switch: another panel is open, or one closed a moment ago and its surface is still fading */
        const fromName = openName || (Date.now() - closedAt < 140 ? closedName : null);
        const from = fromName && fromName !== name ? panelOf(fromName) : null;
        /* the old panel loses is-open only together with its exit class: setBottom and the height reads below flush
           styles, and a flush in between would start its fade on the plain closing curve */
        const prevOpen = openName ? panelOf(openName) : null;
        if (openName) triggerOf(openName)?.setAttribute('aria-expanded', 'false');
        setBottom();
        openName = name;
        openedAt = Date.now();
        if (panel.classList.contains('bizh__panel')) {
          /* anchored dropdowns: keep the whole panel inside the viewport */
          panel.style.setProperty('--biz-dx', '0px');
          const r = panel.getBoundingClientRect();
          const edge = 16;
          const vw = document.documentElement.clientWidth;
          let dx = 0;
          if (r.right > vw - edge) dx = vw - edge - r.right;
          if (r.left + dx < edge) dx = edge - r.left;
          panel.style.setProperty('--biz-dx', Math.round(dx) + 'px');
        }
        if (fx && from && !this.reduced()) {
          /* switch: the surface stays and morphs its height, the content moves in the direction of travel */
          const dir = order(name) > order(fromName) ? 'r' : 'l';
          clearTimeout(this.switchTimer);
          clearTimeout(this.revealTimer);
          /* heights first: the surface's current height (it may be mid move), the old panel's and the new one's */
          const sh = surface.getBoundingClientRect().height;
          const fh = from.offsetHeight;
          const th = panel.offsetHeight;
          panel.classList.remove('is-reveal', 'is-to-l', 'is-to-r', 'is-in-l', 'is-in-r');
          panel.inert = false;
          from.classList.remove('is-open', 'is-from-l', 'is-from-r', 'is-to-l', 'is-to-r', 'is-reveal', 'is-in-l', 'is-in-r');
          from.classList.add(dir === 'r' ? 'is-to-l' : 'is-to-r');
          from.inert = true;
          /* both contents stay inside the surface: the old one's bottom clip closes in as the surface shrinks, the new
             one starts clipped at the surface's height and opens as it grows (same 240 ms curve, CSS) */
          clip(from, fh - th);
          nav.classList.add('is-switching');
          panel.classList.add('is-open', dir === 'r' ? 'is-from-r' : 'is-from-l', dir === 'r' ? 'is-in-r' : 'is-in-l');
          clip(panel, th - sh);
          morph(sh, th);
          getComputedStyle(panel).transform;
          panel.classList.remove('is-from-r', 'is-from-l');
          clip(panel, 0);
          placeInd(name, true);
          /* the incoming columns run up to 180 ms late plus 260 ms (CSS), so the switch state holds for 450 ms */
          this.switchTimer = setTimeout(finishSwitch, 450);
        } else {
          /* first open: the surface appears at once and the content fills in from the left */
          prevOpen?.classList.remove('is-open');
          finishSwitch();
          setSurface(panel.offsetHeight);
          placeInd(name, false);
          panel.classList.add('is-open');
          if (fx && !this.reduced()) {
            panel.classList.add('is-reveal');
            clearTimeout(this.revealTimer);
            this.revealTimer = setTimeout(() => panel.classList.remove('is-reveal'), 450);
          }
        }
        triggerOf(name)?.setAttribute('aria-expanded', 'true');
        nav.classList.add('is-open');
        scrim?.classList.add('is-on');
        openedVia = via;
      };
      const close = (returnFocus) => {
        clearTimeout(this.openTimer); clearTimeout(this.closeTimer);
        if (!openName) return;
        const name = openName;
        openName = null;
        closedName = name;
        closedAt = Date.now();
        panelOf(name)?.classList.remove('is-open', 'is-reveal');
        const t = triggerOf(name);
        if (t) { t.setAttribute('aria-expanded', 'false'); if (returnFocus) t.focus(); }
        nav.classList.remove('is-open');
        scrim?.classList.remove('is-on');
      };
      this.openPanel = open;
      this.closePanel = close;

      triggers.forEach((t) => {
        const name = t.getAttribute('data-nav-trigger');
        this.on(t, 'click', (e) => {
          if (openName === name) {
            /* a mouse click on an item that hover already opened keeps it open; keyboard (detail 0) and touch toggle */
            if (e.detail > 0 && this.canHover() && openedVia === 'hover') return;
            close(false);
          } else open(name, 'click');
        });
        /* hover intent: 100 ms to open, 80 ms to switch to a neighbour while a panel is open. Every mousemove restarts
           the timer, so a pointer that only passes over an item (on its way into a panel or across to the cart) never
           opens or switches it. While a panel is open, a pointer heading down toward it waits 280 ms before a switch, so
           a diagonal path into the panel survives a slow frame. */
        const arm = (e) => {
          if (!this.canHover() || !this.isDesktop()) return;
          clearTimeout(this.closeTimer);
          clearTimeout(this.openTimer);
          if (openName === name) return;
          this.loadImages(panelOf(name));
          if (e && e.type === 'mouseenter') prep(panelOf(name));
          const heading = openName && e && e.movementY > 0;
          this.openTimer = setTimeout(() => open(name, 'hover'), openName ? (heading ? 280 : 80) : 100);
        };
        this.on(t, 'mouseenter', arm);
        this.on(t, 'mousemove', arm, { passive: true });
        this.on(t, 'mouseleave', () => clearTimeout(this.openTimer));
        this.on(t, 'keydown', (e) => {
          if (e.key !== 'ArrowDown') return;
          e.preventDefault();
          if (openName !== name) open(name, 'key');
          else panelOf(name)?.querySelector('a[href], button, input')?.focus();
        });
      });

      /* Images start loading as soon as the pointer or focus reaches the menu, so panels open fully drawn. */
      const list = nav.querySelector('.nav-primary');
      this.on(list, 'pointerenter', hydrate);
      this.on(list, 'focusin', hydrate);
      /* moving onto the logo or the bar actions (search, account, cart) closes a panel that hover opened */
      [nav.querySelector('.nav-logo'), nav.querySelector('.nav-actions')].forEach((el) => this.on(el, 'mouseenter', () => {
        if (!this.canHover() || !openName || openedVia !== 'hover') return;
        clearTimeout(this.openTimer);
        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(() => close(false), 250);
      }));
      nav.querySelectorAll('[data-nav-panel]').forEach((p) => this.on(p, 'mouseenter', () => clearTimeout(this.closeTimer)));

      this.on(nav, 'mouseleave', () => {
        if (!this.canHover() || !openName) return;
        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(() => close(false), 200);
      });
      this.on(nav, 'mouseenter', () => clearTimeout(this.closeTimer));
      this.on(document, 'keydown', (e) => {
        if (e.key !== 'Escape' || !openName) return;
        const inside = nav.contains(document.activeElement);
        close(inside || document.activeElement === document.body);
      });
      this.on(document, 'click', (e) => {
        if (!openName || Date.now() - openedAt < 80) return;
        if (!nav.contains(e.target)) close(false);
      });
      /* keyboard: leaving the open item (its trigger plus panel) closes it */
      this.on(nav, 'focusout', (e) => {
        if (!openName) return;
        const to = e.relatedTarget;
        const item = triggerOf(openName)?.closest('.nav-item');
        if (to && item && !item.contains(to)) close(false);
      });
      this.on(scrim, 'click', () => close(false));
      /* following a link or starting a flow from inside a panel closes the panel first */
      this.on(nav, 'click', (e) => {
        const t = e.target.closest('[data-nav-panel] a[href], [data-nav-panel] [data-search-query]');
        if (t && openName) close(false);
      });
      this.on(window, 'resize', () => {
        if (!this.isDesktop()) { close(false); return; }
        if (openName) { setBottom(); placeInd(openName, false); }
      });
      this.on(window, 'scroll', () => { if (openName) setBottom(); }, { passive: true });
    }

    /* Account click dropdown and keyboard links (AJ 2026-10-12 14:42). */
    initAccount() {
      const wrap = this.querySelector('[data-nav-acct]');
      const button = wrap?.querySelector('[data-nav-acct-btn]');
      const panel = wrap?.querySelector('[data-nav-acct-panel]');
      if (!wrap || !button || !panel || this.closeAcct) return;
      const links = Array.from(panel.querySelectorAll('a[href]'));
      const isOpen = () => wrap.classList.contains('is-open');
      const close = (returnFocus = panel.contains(document.activeElement)) => {
        if (!isOpen()) return;
        wrap.classList.remove('is-open');
        button.setAttribute('aria-expanded', 'false');
        if (returnFocus) button.focus({ preventScroll: true });
        /* Keep fading links out of keyboard navigation and the accessibility tree immediately. */
        panel.inert = true;
      };
      const open = () => {
        if (!this.isDesktop() || isOpen()) return;
        this.closePanel?.(false);
        this.closeHelp?.(false);
        panel.inert = false;
        wrap.classList.add('is-open');
        button.setAttribute('aria-expanded', 'true');
        links[0]?.focus({ preventScroll: true });
      };
      panel.inert = true;
      this.closeAcct = close;
      this.on(button, 'click', () => isOpen() ? close() : open());
      this.on(wrap, 'keydown', (e) => {
        if (e.key === 'Escape' && isOpen()) {
          e.preventDefault();
          e.stopPropagation();
          close(true);
          return;
        }
        if (e.key === 'ArrowDown' && e.target === button && !isOpen()) {
          e.preventDefault();
          open();
          return;
        }
        if (!isOpen() || !links.length || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
        e.preventDefault();
        const current = links.indexOf(document.activeElement);
        let next = 0;
        if (e.key === 'End') next = links.length - 1;
        else if (e.key === 'ArrowDown') next = (current + 1) % links.length;
        else if (e.key === 'ArrowUp') next = current <= 0 ? links.length - 1 : current - 1;
        links[next].focus({ preventScroll: true });
      });
      const outside = (e) => { if (!wrap.contains(e.target)) close(false); };
      this.on(document, 'pointerdown', outside, { capture: true });
      this.on(document, 'click', outside, { capture: true });
      this.on(wrap, 'focusout', (e) => { if (!wrap.contains(e.relatedTarget)) close(false); });
      this.on(panel, 'click', (e) => { if (e.target.closest('a[href]')) close(); });
      this.querySelectorAll('[data-cq-search-open]').forEach((trigger) => this.on(trigger, 'click', () => close(false)));
      this.on(window, 'resize', () => { if (!this.isDesktop()) close(false); });
    }

    /* Need help? in the top bar (snippets/cq-nav-help.liquid, AJ 2026-10-14 18:27, revised 19:16). Desktop: a disclosure
       panel that hangs from the strip; opening it closes the account card and a mega panel, and they close it; Escape,
       an outside click or tabbing out close it, with focus on the first row and back on the button. Phones: the same rows
       open in the menu's own sheet (initMobile, view "help", built once from the panel), so the entrance, the X, Escape
       and closing the other sheets all work as they do for the menu. The chat row shows only with the HubSpot widget.
       While that sheet is open the strip behind it is inert (a modal dialog), so a tap on Help or its chevron lands on the
       dialog's backdrop; a tap inside the button's box closes the sheet too (AJ 2026-10-15 03:41). */
    initHelp() {
      const wrap = this.querySelector('[data-nav-help]');
      const button = wrap?.querySelector('[data-nav-help-btn]');
      const panel = wrap?.querySelector('[data-nav-help-panel]');
      const sheet = this.querySelector('[data-mnav]');
      if (!wrap || !button || !panel || this.closeHelp) return;
      const label = sheet?.getAttribute('aria-label');
      let view = null;
      const isOpen = () => wrap.classList.contains('is-open');
      const syncChat = (scope) => scope.querySelectorAll('[data-nav-help-chat]').forEach((row) => { row.hidden = !window.HubSpotConversations?.widget; });
      const controls = () => button.setAttribute('aria-controls', this.isDesktop() || !sheet ? panel.id : sheet.id);
      const close = (returnFocus = panel.contains(document.activeElement)) => {
        if (!isOpen()) return;
        wrap.classList.remove('is-open');
        button.setAttribute('aria-expanded', 'false');
        panel.inert = true;
        if (returnFocus) button.focus({ preventScroll: true });
      };
      const open = () => {
        this.closeAcct?.(false);
        this.closePanel?.(false);
        syncChat(panel);
        panel.inert = false;
        wrap.classList.add('is-open');
        button.setAttribute('aria-expanded', 'true');
        panel.querySelector('li:not([hidden]) > :is(a, button)')?.focus({ preventScroll: true });
      };
      const openSheet = (event) => {
        if (!this.openMobile) return;
        if (!view) {
          view = document.createElement('div');
          view.className = 'mnav-view nav-help__view';
          view.dataset.mnavView = 'help';
          const title = document.createElement('p');
          title.className = 'mnav-view__title';
          title.textContent = button.querySelector('.nav-help__long')?.textContent.trim() || '';
          view.append(title, ...Array.from(panel.children, (group) => group.cloneNode(true)));
          view.querySelectorAll('.nav-col__head').forEach((head) => { head.className = 'mnav-acct__head'; });
          view.querySelectorAll('.nav-help__row').forEach((row) => row.classList.add('mnav-group'));
          this.on(view, 'click', chat);
          this.addMobileView(view);
        }
        syncChat(view);
        this.openMobile(event, 'help', button);
      };
      const chat = (event) => {
        if (!event.target.closest('[data-nav-help-chat]')) return;
        const hs = window.HubSpotConversations;
        const widget = hs?.widget;
        close(false);
        this.closeMobile?.(false);
        if (!widget) return;
        if (widget.status?.().loaded) widget.open();
        else { hs.on?.('widgetLoaded', () => widget.open()); widget.load?.(); }
      };
      panel.inert = true;
      controls();
      this.closeHelp = close;
      this.on(button, 'click', (event) => {
        if (!this.isDesktop()) openSheet(event);
        else if (isOpen()) close(true);
        else open();
      });
      this.on(sheet, 'close', () => {
        if (button.getAttribute('aria-expanded') === 'true' && !isOpen()) button.setAttribute('aria-expanded', 'false');
        if (label) sheet.setAttribute('aria-label', label);
      });
      /* The strip is inert behind the open sheet, so its taps land on the dialog. A tap on Need help? closes the Help view,
         or, while the menu is open, switches straight to Help in the same tap (AJ 2026-10-21). */
      this.on(sheet, 'click', (event) => {
        if (event.target !== sheet || this.isDesktop() || !this.mobileMode?.()) return;
        const box = button.getBoundingClientRect();
        if (!(event.clientX >= box.left - 4 && event.clientX <= box.right + 4 && event.clientY >= box.top && event.clientY <= box.bottom)) return;
        if (this.mobileMode() === 'help') this.closeMobile?.(true, event);
        else openSheet(event);
      });
      this.on(wrap, 'keydown', (e) => {
        if (e.key !== 'Escape' || !isOpen()) return;
        e.preventDefault();
        e.stopPropagation();
        close(true);
      });
      const outside = (e) => { if (!wrap.contains(e.target)) close(false); };
      this.on(document, 'pointerdown', outside, { capture: true });
      this.on(document, 'click', outside, { capture: true });
      this.on(wrap, 'focusout', (e) => { if (e.relatedTarget && !wrap.contains(e.relatedTarget)) close(false); });
      /* the strip scrolls away under the sticky bar; the panel goes with it rather than float over the bar */
      this.on(window, 'scroll', () => { if (isOpen() && button.getBoundingClientRect().bottom < 0) close(false); }, { passive: true });
      this.on(this.mqDesktop, 'change', () => { close(false); controls(); });
      this.on(panel, 'click', (e) => {
        if (e.target.closest('a[href]')) close(false);
        else chat(e);
      });
    }

    /* ---------- ZIP hardness map handoff ---------- */
    initZip(scope) {
      (scope || this).querySelectorAll('[data-nav-zip]').forEach((card) => {
        const form = card.querySelector('[data-zip-form]');
        const input = form?.querySelector('input');
        const out = card.querySelector('[data-zip-result]');
        if (!form || !input || !out) return;
        const clearError = () => {
          card.classList.remove('is-error');
          input.removeAttribute('aria-invalid');
          out.replaceChildren();
        };
        this.on(input, 'input', () => {
          input.value = input.value.replace(/\D/g, '').slice(0, 5);
          clearError();
        });
        this.on(form, 'submit', (e) => {
          const zip = input.value.trim();
          if (!/^\d{5}$/.test(zip)) {
            e.preventDefault();
            card.classList.add('is-error');
            input.setAttribute('aria-invalid', 'true');
            const message = document.createElement('div');
            message.className = 'nav-zr nav-zr--msg';
            message.textContent = card.dataset.tInvalid;
            out.replaceChildren(message);
            input.focus({ preventScroll: true });
            return;
          }
          clearError();
          input.value = zip;
          /* Native GET opens the map's ZIP results, preserving leading zeroes and browser history. */
        });
      });
    }

    /* ---------- Phone sheet ---------- */
    initMobile() {
      const root = this.querySelector('[data-mnav]');
      const opener = this.querySelector('[data-mnav-open]');
      const sheet = root?.querySelector('.mnav__sheet');
      const rootView = root?.querySelector('[data-mnav-view="root"]');
      if (!root || !sheet || !rootView) { if (opener) opener.hidden = true; return; }
      const backButton = root.querySelector('[data-mnav-back]');
      const toggle = root.querySelector('[data-mnav-toggle]');
      const menuLabel = root.getAttribute('aria-label');
      let views = [];
      let viewByName = new Map();
      let triggers = [];
      const bound = new WeakSet();
      /* Move existing nodes once, not clones. ZIP/finder listeners and form state survive; a hidden parent can never
         hide or make a child view inert. Reconnecting a section is safe because these are already sheet children. */
      const collect = () => {
        views = Array.from(root.querySelectorAll('[data-mnav-view]'));
        viewByName = new Map(views.map((view) => [view.dataset.mnavView, view]));
        views.forEach((view) => { if (view.parentElement !== sheet) sheet.append(view); });
        triggers = Array.from(root.querySelectorAll('[data-mnav-target]'));
        triggers.forEach((trigger) => {
          if (bound.has(trigger)) return;
          bound.add(trigger);
          this.on(trigger, 'click', (event) => forward(trigger, event));
        });
      };
      /* A1-04 (2026-10-14): outside the theme editor the phone panels ship inside <template data-mnav-tpl>
         (snippets/cq-nav-mobile.liquid), so a page that never opens the phone menu never builds those elements. The
         first open moves them into the sheet once, then wires their ZIP checks and rows exactly as at start up. The
         desktop bar keeps the same links, so crawlers and the no script page lose nothing. */
      const mount = () => {
        const tpl = root.querySelector('template[data-mnav-tpl]');
        if (!tpl) return;
        const host = tpl.parentElement;
        tpl.replaceWith(tpl.content);
        /* Tracking and consent scripts (HubSpot, Shopify) add their parameters to cross-site links at page load, which
           links still in the template missed. Every phone link is also in the desktop bar, so copy its current address. */
        const desk = Array.from(this.querySelectorAll('.nav-primary a[href^="http"]'), (a) => a.getAttribute('href'));
        host.querySelectorAll('a[href^="http"]').forEach((a) => {
          const href = a.getAttribute('href');
          const match = desk.find((d) => d.length > href.length && d.startsWith(href) && /^[?&].*(__hs|consent|_gl=|_ga=)/.test(d.slice(href.length)));
          if (match) a.setAttribute('href', match);
        });
        this.initZip(host);
        collect();
        syncState();
        settleViews();
      };
      const durationMs = (token, fallback) => {
        const value = getComputedStyle(root).getPropertyValue(token).trim();
        const ms = value.endsWith('ms') ? parseFloat(value) : parseFloat(value) * 1000;
        return Number.isFinite(ms) ? ms : fallback;
      };
      const sheetMs = durationMs('--mnav-t-sheet', 600);
      const viewMs = durationMs('--mnav-t-view', 540);
      const motionClasses = ['is-enter-forward', 'is-exit-forward', 'is-enter-back', 'is-exit-back'];
      const history = [];
      let currentView = rootView;
      let lastFocus = null;
      /* the control that opened the sheet from outside the bar (the top bar's Need help?): its aria-expanded, and with it
         its chevron, turns back as the sheet starts to close, the way the menu icon does */
      let invoker = null;
      let motionTimer = null;
      let finishMotion = null;
      let sequence = 0;
      let closing = false;
      const instant = () => this.reduced() || root.classList.contains('is-instant');
      const inputMode = (event) => {
        if (event) root.classList.toggle('is-instant', event.type === 'keydown' || event.type === 'cancel' || event.detail === 0);
      };
      const unlock = () => {
        window.cqScrollLock?.unlock(root);
        if (!document.querySelector('.cqp.mnav[open], cq-search dialog[open]')) document.documentElement.classList.remove('cqp-locked');
      };
      const syncState = () => {
        root.dataset.view = currentView.dataset.mnavView;
        root.dataset.depth = history.length;
        root.classList.toggle('is-deep', history.length > 0);
        if (backButton) backButton.hidden = history.length === 0;
        const trail = new Set([...history.map((entry) => entry.view.dataset.mnavView), currentView.dataset.mnavView]);
        triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', trail.has(trigger.dataset.mnavTarget) ? 'true' : 'false'));
      };
      /* Two top-level modes share the sheet: the menu, and the top bar's Need help? view. The toggle over the hamburger
         shows the X and closes in the menu; in Help it shows the hamburger and switches to the menu (AJ 2026-10-21). */
      const syncMode = () => {
        const help = !history.length && currentView.dataset.mnavView === 'help';
        const shown = root.open && !closing;
        root.dataset.mode = help ? 'help' : 'menu';
        opener?.setAttribute('aria-expanded', shown && !help ? 'true' : 'false');
        invoker?.setAttribute('aria-expanded', shown && help ? 'true' : 'false');
        if (toggle) {
          toggle.setAttribute('aria-label', help ? toggle.dataset.labelOpen : toggle.dataset.labelClose);
          toggle.setAttribute('aria-expanded', help ? 'false' : 'true');
        }
        const title = help && currentView.querySelector('.mnav-view__title')?.textContent.trim();
        if (title) root.setAttribute('aria-label', title);
        else if (menuLabel) root.setAttribute('aria-label', menuLabel);
      };
      /* Pin the toggle onto the header hamburger's box, so it never jumps when the sheet opens. */
      const placeToggle = () => {
        if (!toggle || !opener) return;
        const box = opener.getBoundingClientRect();
        if (!box.width) return;
        root.style.setProperty('--mnav-tgl-x', box.left + 'px');
        root.style.setProperty('--mnav-tgl-y', box.top + 'px');
      };
      /* Switch between the menu and the Help view inside an open sheet, in one tap, with no close in between. */
      const switchTo = (event, viewName, returnTo) => {
        const view = (viewName && viewByName.get(viewName)) || rootView;
        if (!root.open || closing || (view === currentView && !history.length)) return;
        inputMode(event);
        if (returnTo) invoker = returnTo;
        const toMenu = view === rootView;
        history.length = 0;
        showView(view, toMenu ? 'back' : 'forward', toggle, 0);
        syncMode();
      };
      const settleViews = () => {
        views.forEach((view) => {
          view.classList.remove(...motionClasses);
          view.hidden = view !== currentView;
          view.inert = view !== currentView;
        });
      };
      /* One pending motion for either a view change or dialog exit. Every new action cancels its predecessor; logical
         history/focus changes immediately, so rapid Back/Escape always means exactly one level per action. */
      const cancelMotion = () => {
        sequence++;
        clearTimeout(motionTimer);
        motionTimer = finishMotion = null;
        settleViews();
      };
      const settleMotion = () => {
        clearTimeout(motionTimer);
        const done = finishMotion;
        motionTimer = finishMotion = null;
        done?.();
      };
      const deferMotion = (done, delay = sheetMs) => {
        const seq = sequence;
        finishMotion = done;
        motionTimer = setTimeout(() => { if (seq === sequence) settleMotion(); }, delay);
      };
      const restoreFocus = () => {
        if (lastFocus?.focus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
      };
      const reset = () => {
        cancelMotion();
        closing = false;
        history.length = 0;
        currentView = rootView;
        views.forEach((view) => { view.scrollTop = 0; });
        root.classList.remove('is-open');
        opener?.setAttribute('aria-expanded', 'false');
        syncState();
        settleViews();
        root.dataset.mode = 'menu';
        if (menuLabel) root.setAttribute('aria-label', menuLabel);
        unlock();
      };
      const showView = (view, direction, focusTarget, scrollTop) => {
        cancelMotion();
        const previous = currentView;
        currentView = view;
        currentView.hidden = false;
        currentView.inert = false;
        previous.inert = true;
        currentView.scrollTop = scrollTop;
        syncState();
        root.classList.add('is-open');
        focusTarget?.focus({ preventScroll: true });
        if (instant()) { settleViews(); return; }
        previous.classList.add('is-exit-' + direction);
        currentView.classList.add('is-enter-' + direction);
        deferMotion(settleViews, viewMs);
      };
      const forward = (trigger, event) => {
        if (!root.open || closing || trigger.closest('[data-mnav-view]') !== currentView) return;
        const view = viewByName.get(trigger.dataset.mnavTarget);
        if (!view || view === currentView) return;
        inputMode(event);
        history.push({ view: currentView, trigger, scrollTop: currentView.scrollTop });
        showView(view, 'forward', backButton, 0);
      };
      const back = (event) => {
        if (!root.open || closing || !history.length) return;
        inputMode(event);
        const parent = history.pop();
        showView(parent.view, 'back', parent.trigger, parent.scrollTop);
      };
      /* viewName opens the sheet straight on that view (the top bar's Need help?), and returnTo gets focus back. */
      const open = (event, viewName, returnTo) => {
        if (this.isDesktop()) return;
        if (root.open && !closing) { switchTo(event, viewName, returnTo); return; }
        inputMode(event);
        const reversing = root.open;
        const start = viewName && viewByName.get(viewName);
        cancelMotion();
        closing = false;
        this.closePanel?.(false);
        if (!reversing) {
          /* Safari pointer activation need not focus the button. Programmatic callers retain their focused control. */
          lastFocus = returnTo || (event?.currentTarget === opener ? opener : document.activeElement);
          invoker = returnTo || null;
          root.style.setProperty('--mnav-top', Math.max(0, this.nav?.getBoundingClientRect().top || 0) + 'px');
          placeToggle();
          mount();
          this.loadImages(root);
          if (start) { currentView = start; syncState(); settleViews(); }
          root.showModal();
        }
        syncMode();
        toggle?.focus({ preventScroll: true });
        window.cqScrollLock?.lock(root, { root, isOpen: () => root.open });
        document.documentElement.classList.add('cqp-locked');
        const seq = sequence;
        const reveal = () => { if (seq === sequence && root.open) root.classList.add('is-open'); };
        if (instant() || reversing) reveal();
        else requestAnimationFrame(() => requestAnimationFrame(reveal));
      };
      const close = (restore, event) => {
        inputMode(event);
        if (!root.open) { reset(); return; }
        if (closing && restore !== false && !instant()) return;
        cancelMotion();
        closing = true;
        root.classList.remove('is-open');
        syncMode();
        const finish = () => {
          root.close();
          reset();
          if (restore !== false) restoreFocus();
        };
        if (restore === false || instant()) finish();
        else deferMotion(finish);
      };
      collect();
      syncState();
      settleViews();
      this.on(backButton, 'click', back);
      this.on(opener, 'click', (event) => (root.open && !closing ? close(true, event) : open(event)));
      this.on(root, 'pointerdown', () => root.classList.remove('is-instant'));
      this.on(root, 'keydown', (event) => { inputMode(event); settleMotion(); });
      this.on(root, 'cancel', (event) => {
        event.preventDefault();
        if (history.length) back(event);
        else close(true, event);
      });
      /* Ignore a queued native close event if the modal has already reopened. */
      this.on(root, 'close', () => {
        if (root.open) return;
        const restore = root.classList.contains('is-open');
        reset();
        if (restore) restoreFocus();
      });
      this.on(toggle, 'click', (event) => {
        if (closing) open(event);
        else if (root.dataset.mode === 'help') switchTo(event, null);
        else close(true, event);
      });
      this.on(root, 'click', (event) => {
        if (event.target.closest('[data-mnav-close]')) {
          if (closing) open(event);
          else close(true, event);
          return;
        }
        if (event.target.closest('a[href]')) close(false);
      });
      this.on(window, 'resize', () => { if (this.isDesktop()) close(false); else if (root.open) placeToggle(); });
      /* WebKit can dispatch resize before matchMedia updates. All listeners share section lifecycle cleanup. */
      this.on(this.mqDesktop, 'change', (event) => { if (event.matches) close(false); });
      this.on(this.mqReduced, 'change', (event) => { if (event.matches) settleMotion(); });
      this.closeMobile = close;
      this.openMobile = open;
      this.mobileMode = () => (root.open && !closing ? root.dataset.mode : '');
      this.addMobileView = (view) => { root.append(view); collect(); settleViews(); };
      this.mobileOpen = () => root.open;
    }

    closeAll() {
      this.closeAcct?.(false);
      this.closeHelp?.(false);
      this.closePanel?.(false);
      this.closeMobile?.(false);
    }
  });
}
